import { type RefObject, useEffect, useState } from 'react'

/**
 * 要素がビューポート（または親 root）に入ったかを返すフック。
 *
 * 使い方:
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null)
 * const inView = useInView(ref)
 * // inView が true になったら重い処理を発火
 * ```
 *
 * `once: true` を渡すと一度 true になったら以降は true 固定（lazy load 向け）。
 */
export function useInView(
  ref: RefObject<Element | null>,
  options: { once?: boolean; rootMargin?: string } = {},
): boolean {
  const { once = true, rootMargin = '200px' } = options
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const target = ref.current
    if (!target) return
    // SSR / 古いブラウザ向けフォールバック: IntersectionObserver が無ければ即時 true 扱い
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true)
            if (once) observer.disconnect()
          } else if (!once) {
            setInView(false)
          }
        }
      },
      { rootMargin },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [ref, once, rootMargin])

  return inView
}
